// Lazy Loading Handler
// - Lazy-load für Elemente mit `data-src`
// - Verwendet IntersectionObserver für Performance
// - Video-Setup für proxy loading via HTML

type LoadableElement = HTMLImageElement | HTMLIFrameElement | HTMLElement;

type VideoSources = {
	desktop: Record<string, string>;
	mobile: Record<string, string>;
};

// Speichern der gehashten Video-Pfade pro Video (Map für mehrere Videos)
const videoSourcesMap = new Map<HTMLVideoElement, VideoSources>();
// Bereits gestartete Videos (Hero-Video startet früher als die übrigen)
const startedVideos = new WeakSet<HTMLVideoElement>();

const PAUSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';
const PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>';

function isImage(el: Element): el is HTMLImageElement {
	return el.tagName.toLowerCase() === 'img';
}

function loadResource(el: LoadableElement): Promise<void> {
	return new Promise((resolve) => {
		if (isImage(el)) {
			const img = el as HTMLImageElement;
			const src = img.dataset.src;
			if (!src) return resolve();
			img.onload = () => img.classList.add('loaded');
			img.onerror = () => img.classList.add('error');
			img.src = src;
			delete img.dataset.src;
			return resolve();
		}

		const src = el.getAttribute('data-src');
		if (src) {
			if (el instanceof HTMLIFrameElement) {
				el.src = src;
			} else {
				el.setAttribute('style', (el.getAttribute('style') || '') + `background-image:url('${src}')`);
			}
			el.removeAttribute('data-src');
		}
		resolve();
	});
}

/**
 * Capture gehashte Video-Pfade aus HTML und src-Attribute entfernen
 * Wird bei DOMContentLoaded aufgerufen
 * Funktioniert mit allen video[data-lazy-video] Elementen
 */
export function captureVideoSources(): void {
	const videos = document.querySelectorAll('video[data-lazy-video]') as NodeListOf<HTMLVideoElement>;
	
	if (!videos.length) return;

	videos.forEach((video) => {
		// Dekoratives Video ohne Ton: für Screenreader ausblenden.
		// Gestartet wird per setupLazyVideos(), nicht per autoplay-Attribut.
		video.setAttribute('aria-hidden', 'true');
		video.autoplay = false;

		const sources: VideoSources = {
			desktop: {},
			mobile: {}
		};

		Array.from(video.querySelectorAll('source')).forEach((source) => {
			const src = source.getAttribute('src');
			const type = source.getAttribute('type') || '';
			if (!src) return;

			const isMobile = source.getAttribute('data-mobile') === 'true';
			if (isMobile) {
				sources.mobile[type] = src;
			} else {
				sources.desktop[type] = src;
			}
			
			// src entfernen, damit Video nicht sofort geladen wird
			source.removeAttribute('src');
		});

		videoSourcesMap.set(video, sources);
	});
}

/**
 * Setup der Lazy-Videos mit der richtigen Variante (mobile/desktop)
 * Ohne Argument alle noch nicht gestarteten Videos, sonst nur die übergebenen
 * (das Hero-Video startet schon bei DOMContentLoaded, der Rest bei window load)
 */
export function setupLazyVideos(only?: HTMLVideoElement[]): void {
	const isMobile = window.matchMedia('(max-width:768px)').matches;
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	videoSourcesMap.forEach((sources, video) => {
		if (only && !only.includes(video)) return;
		if (startedVideos.has(video)) return;
		startedVideos.add(video);

		const variant = isMobile ? sources.mobile : sources.desktop;

		Array.from(video.querySelectorAll('source')).forEach((source) => {
			const type = source.getAttribute('type') || '';
			if (variant[type]) {
				source.setAttribute('src', variant[type]);
			}
		});

		if (reduceMotion) {
			// "Bewegung reduzieren": nur das erste Bild zeigen, abspielen per Button
			video.preload = 'auto';
			video.load();
		} else {
			video.load();
			video.play().catch(() => {});
		}
		addVideoToggle(video);
	});
}

/**
 * Anhalten/Abspielen-Button für ein Video (WCAG 2.2.2: bewegte Inhalte,
 * die länger als 5 Sekunden laufen, müssen sich anhalten lassen)
 */
function addVideoToggle(video: HTMLVideoElement): void {
	const wrapper = video.parentElement;
	if (!wrapper || wrapper.querySelector('.video-toggle')) return;

	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'video-toggle';

	const update = () => {
		button.setAttribute('aria-label', video.paused ? 'Video abspielen' : 'Video anhalten');
		button.innerHTML = video.paused ? PLAY_ICON : PAUSE_ICON;
	};

	button.addEventListener('click', () => {
		if (video.paused) video.play().catch(() => {});
		else video.pause();
	});
	video.addEventListener('play', update);
	video.addEventListener('pause', update);
	update();
	wrapper.append(button);
}

/**
 * Wartet, bis das Video ein erstes Bild hat, höchstens maxWait Millisekunden
 * (für den Preloader, damit das Video nicht erst nachträglich "reinploppt")
 */
export function waitForVideo(video: HTMLVideoElement, maxWait: number): Promise<void> {
	if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA || maxWait <= 0) return Promise.resolve();

	return new Promise((resolve) => {
		let timer = 0;
		const done = () => {
			window.clearTimeout(timer);
			resolve();
		};
		timer = window.setTimeout(done, maxWait);
		video.addEventListener('loadeddata', done, { once: true });
	});
}

export function lazyLoadImages(root: ParentNode = document): Promise<void> {
	return new Promise((resolve) => {
		const nodes = Array.from(root.querySelectorAll('[data-src]')) as Element[];
		if (!nodes.length) {
			resolve();
			return;
		}

		if ('IntersectionObserver' in window) {
			const io = new IntersectionObserver((entries, obs) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const el = entry.target as LoadableElement;
					loadResource(el).catch(() => {});
					obs.unobserve(entry.target);
				});
			}, { root: null, threshold: 0 });

			nodes.forEach(n => io.observe(n));
			resolve(); // IntersectionObserver Setup fertig
		} else {
			// Fallback: load all immediately
			Promise.all(nodes.map(n => loadResource(n as LoadableElement)))
				.then(() => resolve())
				.catch(() => resolve());
		}
	});
}
